import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const logoUrl = process.env.PUBLIC_URL + '/logo.png';

export default function ResetPassword() {
  const { user, loading: authLoading, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const urlTokens = useMemo(() => {
    const hash = typeof location?.hash === 'string' ? location.hash : '';
    const search = typeof location?.search === 'string' ? location.search : '';
    return `${hash}${search}`;
  }, [location?.hash, location?.search]);

  const hasRecoveryTokens = useMemo(() => {
    // Supabase recovery links typically contain access/refresh tokens and `type=recovery`.
    return /access_token=|refresh_token=|type=recovery|provider_token=|code=/.test(urlTokens);
  }, [urlTokens]);

  useEffect(() => {
    // Reset errors/messages when the tokenized URL changes (e.g. user opens another reset link).
    setError('');
    setMessage('');
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTokens]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      await updatePassword(password);
      setMessage('Password updated. Signing you in…');
      window.history.replaceState(null, '', window.location.pathname);
      setTimeout(() => navigate('/', { replace: true }), 1000);
    } catch (err) {
      const raw = err?.message || '';
      const lower = raw.toLowerCase();
      if (lower.includes('expired') || lower.includes('invalid') || lower.includes('recovery')) {
        setError('This reset link is invalid or has expired. Request a new reset link from the sign-in page.');
      } else if (lower.includes('password')) {
        setError(raw || 'Could not update password.');
      } else {
        setError('Could not update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleBackToLogin() {
    signOut().catch(() => {});
    navigate('/', { replace: true });
  }

  if (authLoading) {
    return (
      <div className="login-screen">
        <div className="login-bg-gradient" aria-hidden />
        <div className="login-card">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-bg-gradient" aria-hidden />
        <div className="login-card">
          <div className="login-logo-wrap">
            <img src={logoUrl} alt="CDT" className="login-logo" />
          </div>
          <h1 className="login-title">{hasRecoveryTokens ? 'Validating reset link…' : 'Reset password'}</h1>
          <p className="login-subtitle">
            {hasRecoveryTokens ? 'Please wait while we validate your reset link.' : 'Invalid or expired link. Request a new reset link from the sign-in page.'}
          </p>
          <button type="button" className="login-submit" onClick={handleBackToLogin}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-bg-gradient" aria-hidden />
      <div className="login-card">
        <div className="login-logo-wrap">
          <img src={logoUrl} alt="CDT" className="login-logo" />
        </div>
        <h1 className="login-title">Set new password</h1>
        <p className="login-subtitle">Enter your new password below.</p>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="reset-password">New password</label>
            <input
              id="reset-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className={error ? 'input-error' : ''}
            />
          </div>
          <div className="login-field">
            <label htmlFor="reset-password-confirm">Confirm password</label>
            <input
              id="reset-password-confirm"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className={error ? 'input-error' : ''}
            />
          </div>
          {error && <p className="login-error">{error}</p>}
          {message && <p className="login-message">{message}</p>}
          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? <span className="login-spinner" aria-hidden /> : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}
